import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Filter, X } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

export interface FilterValues {
  dateRange?: { from: Date; to: Date };
  status?: string;
  category?: string;
  costCenter?: string;
  vehicle?: string;
  driver?: string;
  [key: string]: any;
}

interface FilterOption {
  label: string;
  value: string;
}

interface FilterConfig {
  dateRange?: boolean;
  status?: FilterOption[];
  category?: FilterOption[];
  costCenter?: FilterOption[];
  vehicle?: FilterOption[];
  driver?: FilterOption[];
  customFilters?: {
    name: string;
    label: string;
    options: FilterOption[];
  }[];
}

interface AdvancedFiltersProps {
  config: FilterConfig;
  onFilterChange: (filters: FilterValues) => void;
  initialFilters?: FilterValues;
}

export function AdvancedFilters({ config, onFilterChange, initialFilters = {} }: AdvancedFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState<FilterValues>(initialFilters);

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    onFilterChange(filters);
    setIsOpen(false);
  };

  const clearFilters = () => {
    setFilters({});
    onFilterChange({});
  };

  const hasActiveFilters = Object.keys(filters).some(key => {
    const value = filters[key];
    return value !== undefined && value !== null && value !== '';
  });

  return (
    <div className="flex gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Filter className="h-4 w-4" />
            Filtros Avançados
            {hasActiveFilters && (
              <span className="ml-1 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                {Object.keys(filters).filter(k => filters[k]).length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px]" align="start">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {config.dateRange && (
                  <div className="space-y-2">
                    <Label>Período</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !filters.dateRange && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filters.dateRange?.from ? (
                            filters.dateRange.to ? (
                              <>
                                {format(filters.dateRange.from, "dd/MM/yyyy", { locale: ptBR })} -{" "}
                                {format(filters.dateRange.to, "dd/MM/yyyy", { locale: ptBR })}
                              </>
                            ) : (
                              format(filters.dateRange.from, "dd/MM/yyyy", { locale: ptBR })
                            )
                          ) : (
                            <span>Selecione o período</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="range"
                          selected={filters.dateRange}
                          onSelect={(range) => handleFilterChange('dateRange', range)}
                          locale={ptBR}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {config.status && (
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={filters.status || "all"} onValueChange={(v) => handleFilterChange('status', v === "all" ? undefined : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos os status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {config.status.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {config.category && (
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select value={filters.category || "all"} onValueChange={(v) => handleFilterChange('category', v === "all" ? undefined : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas as categorias" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {config.category.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {config.costCenter && (
                  <div className="space-y-2">
                    <Label>Centro de Custo</Label>
                    <Select value={filters.costCenter || "all"} onValueChange={(v) => handleFilterChange('costCenter', v === "all" ? undefined : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos os centros" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {config.costCenter.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {config.customFilters?.map(filter => (
                  <div key={filter.name} className="space-y-2">
                    <Label>{filter.label}</Label>
                    <Select 
                      value={(filters[filter.name] as string) || "all"} 
                      onValueChange={(v) => handleFilterChange(filter.name, v === "all" ? undefined : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={`Todos`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {filter.options.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}

                <div className="flex gap-2 pt-4">
                  <Button onClick={applyFilters} className="flex-1">
                    Aplicar Filtros
                  </Button>
                  <Button variant="outline" onClick={clearFilters}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </PopoverContent>
      </Popover>

      {hasActiveFilters && (
        <Button variant="ghost" onClick={clearFilters} className="gap-2">
          <X className="h-4 w-4" />
          Limpar Filtros
        </Button>
      )}
    </div>
  );
}
